<template>
  <BaseOverlay @click.native.self.exact="closeModal">
    <div class="side-modal" :style="style">
      <div class="side-modal__header modal-title">
        <slot name="title"/>
        <div v-if="showCloseButton" class="side-modal__close" @click="closeModal">
          <img class="side-modal__icon-cross" src="/img/renova-dashboard/icons/x.svg" alt="x"/>
        </div>
      </div>
      <div class="side-modal__content" :class="{ 'side-modal__content--fullHeight': !showFooter }">
        <slot/>
      </div>
      <div v-if="showFooter" class="side-modal__footer">
        <slot name="footer"/>
      </div>
    </div>
    <BaseDialog
      v-if="confirmDialogOpen"
      :width="'444px'"
      :text-align="'left'"
      :footer-align="'flex-end'"
      :title="$t('adminTitles.AreYouSureToCancelOperation')"
      :text="$t('adminTitles.AllUnsavedDataWillBeLost')"
      :confirm-button-text="$t('buttons.KeepEditing')"
      :cancel-button-text="$t('buttons.close')"
      @close="$emit('close')"
      @confirm="confirmDialogOpen = null"
    />
  </BaseOverlay>
</template>

<script>
import BaseOverlay from '@/views/RenovaDashboard/components/ui-kit/BaseOverlay';
import BaseDialog from '@/views/RenovaDashboard/components/ui-kit/BaseDialog';

/**
 * How many side modals are open. Module scope on purpose: the lock is on
 * `document.body`, which every instance shares, so the count has to be shared
 * too.
 */
let openModalCount = 0;

export default {
  name: 'BaseSideModal',
  components: {
    BaseDialog,
    BaseOverlay,
  },
  emits: ['close'],
  props: {
    width: {
      type: String,
      default: '500px',
      required: false,
    },
    showCloseButton: {
      type: Boolean,
      default: true,
      required: false,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    confirmCloseDialog: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      confirmDialogOpen: false,
      /** Whether THIS instance is currently holding the scroll lock. */
      holdsScrollLock: false,
    };
  },
  computed: {
    style() {
      return {
        'max-width': this.width,
      };
    },
  },
  mounted() {
    this.lockScroll();
  },
  beforeDestroy() {
    this.releaseScroll();
  },
  methods: {
    /**
     * Counted, because these stack.
     *
     * It used to set the lock on mount and clear it outright on close, so a
     * modal opened from inside another one unlocked the page the moment it
     * closed — leaving the outer one open over a body that scrolls behind it.
     * Each instance now holds at most one claim and the page stays locked
     * until the last of them lets go.
     */
    lockScroll() {
      if (this.holdsScrollLock) return;
      this.holdsScrollLock = true;
      openModalCount += 1;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    },
    releaseScroll() {
      if (!this.holdsScrollLock) return;
      this.holdsScrollLock = false;
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount > 0) return;
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
    },
    closeModal() {
      if (this.confirmCloseDialog) {
        this.confirmDialogOpen = true;
      } else {
        this.$emit('close');
        // Released here as well as in beforeDestroy: a parent that keeps this
        // mounted after `close` would otherwise leave the page locked. Both
        // paths are idempotent, so the count cannot go wrong whichever runs.
        this.releaseScroll();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.side-modal {
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  min-height: 100%;
  background: var(--white);
  z-index: 1001;
  overflow-y: auto;
  background-color: #ffffff;

  &__header {
    border-bottom: 1px solid var(--solid-lavender-grey);
    color: var(--solid-black-purple);
    margin: 16px 24px 24px;
    padding-bottom: 16px;
  }

  &__close {
    cursor: pointer;
    position: absolute;
    top: 16px;
    right: 22px;

    &-icon-cross {
      width: 20px;
      height: 20px;
    }
  }

  &__content {
    padding: 0 24px;
    height: calc(100% - 172px);
    overflow: hidden;
    overflow-y: scroll;

    &--fullHeight {
      height: calc(100% - 82px);
    }
  }

  &__footer {
    display: flex;
    gap: 16px;
    justify-content: space-between;
    align-items: center;
    padding: 24px;
    box-shadow: 0px -4px 4px 0px #00000005;
  }
}

@media screen and (max-width: 668px) {
  .side-modal__footer {
    flex-wrap: wrap;
  }
}
</style>
